/**
 * The initial Frappe password for a user created from Bosun.
 *
 * It is deliberately unrecoverable. `auth.create_user` requires the field, but Clerk is the
 * sign-in path, so the Frappe password is vestigial — nobody will ever present it. A password
 * nobody knows cannot be leaked, reused, or read over someone's shoulder, so the value is
 * generated here, sent once, and never returned to the caller (see `useUsers.ts`).
 *
 * If an account ever genuinely needs Frappe Desk access, recover through a Frappe admin reset or
 * the password-reset email — outbound mail is confirmed working (`Rfqai` is `default_outgoing`).
 *
 * `crypto.getRandomValues` rather than `Math.random`: this is a credential, and `Math.random` is
 * not a CSPRNG. It is available in every browser this app supports and in Node's test runner.
 */

const LOWER = 'abcdefghijkmnopqrstuvwxyz'
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
const DIGIT = '23456789'
const SYMBOL = '!@#$%^&*-_'
const ALL = LOWER + UPPER + DIGIT + SYMBOL

/** Long enough that the character-class guarantees below cost nothing in entropy terms. */
const LENGTH = 28

function pick(alphabet: string, count: number): string[] {
  const bytes = new Uint32Array(count)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]!)
}

export function generateInitialPassword(): string {
  // One of each class up front so the result satisfies any policy, then fill from the full
  // alphabet, then shuffle — otherwise the first four positions would be predictable by class.
  const chars = [
    ...pick(LOWER, 1),
    ...pick(UPPER, 1),
    ...pick(DIGIT, 1),
    ...pick(SYMBOL, 1),
    ...pick(ALL, LENGTH - 4),
  ]
  const order = new Uint32Array(chars.length)
  crypto.getRandomValues(order)
  return chars
    .map((c, i) => ({ c, k: order[i]! }))
    .sort((a, b) => a.k - b.k)
    .map((x) => x.c)
    .join('')
}
